import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TABLE_MAX_COLS, TABLE_MAX_ROWS } from '../../utils/programSlideTableBlock';

export type EvaluationPlanSheetTableCreateInput = {
  rowCount: number;
  colCount: number;
};

const GRID_PREVIEW_ROWS = 8;
const GRID_PREVIEW_COLS = 10;

function clampDimension(value: number, max: number): number {
  if (!Number.isFinite(value)) return 1;
  return Math.min(max, Math.max(1, Math.round(value)));
}

const EvaluationPlanSheetTableCreateModal: React.FC<{
  open: boolean;
  backdropZIndex?: number;
  onClose: () => void;
  onSubmit: (input: EvaluationPlanSheetTableCreateInput) => void;
}> = ({ open, backdropZIndex, onClose, onSubmit }) => {
  const [rowCount, setRowCount] = useState(3);
  const [colCount, setColCount] = useState(3);
  const [previewRows, setPreviewRows] = useState(3);
  const [previewCols, setPreviewCols] = useState(3);
  const [isGridDragging, setIsGridDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setRowCount(3);
    setColCount(3);
    setPreviewRows(3);
    setPreviewCols(3);
    setIsGridDragging(false);
    setError(null);
  }, [open]);

  const applyGridSelection = useCallback((rowIndex: number, colIndex: number) => {
    const rows = clampDimension(rowIndex + 1, TABLE_MAX_ROWS);
    const cols = clampDimension(colIndex + 1, TABLE_MAX_COLS);
    setPreviewRows(rows);
    setPreviewCols(cols);
    setRowCount(rows);
    setColCount(cols);
    setError(null);
  }, []);

  const resolveGridCellFromPointer = useCallback((clientX: number, clientY: number) => {
    const grid = gridRef.current;
    if (!grid) return null;
    const rect = grid.getBoundingClientRect();
    if (clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom) {
      return null;
    }
    const cellW = rect.width / GRID_PREVIEW_COLS;
    const cellH = rect.height / GRID_PREVIEW_ROWS;
    const col = Math.min(GRID_PREVIEW_COLS - 1, Math.max(0, Math.floor((clientX - rect.left) / cellW)));
    const row = Math.min(GRID_PREVIEW_ROWS - 1, Math.max(0, Math.floor((clientY - rect.top) / cellH)));
    return { row, col };
  }, []);

  useEffect(() => {
    if (!isGridDragging) return;
    const handlePointerMove = (event: PointerEvent) => {
      const cell = resolveGridCellFromPointer(event.clientX, event.clientY);
      if (!cell) return;
      applyGridSelection(cell.row, cell.col);
    };
    const handlePointerUp = () => setIsGridDragging(false);
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [applyGridSelection, isGridDragging, resolveGridCellFromPointer]);

  if (!open) return null;

  const handleSubmit = () => {
    const rows = clampDimension(rowCount, TABLE_MAX_ROWS);
    const cols = clampDimension(colCount, TABLE_MAX_COLS);
    if (rows < 1 || rows > TABLE_MAX_ROWS) {
      setError(`행 수는 1~${TABLE_MAX_ROWS} 사이여야 합니다.`);
      return;
    }
    if (cols < 1 || cols > TABLE_MAX_COLS) {
      setError(`열 수는 1~${TABLE_MAX_COLS} 사이여야 합니다.`);
      return;
    }
    onSubmit({ rowCount: rows, colCount: cols });
  };

  const handleRowInput = (value: number) => {
    const rows = clampDimension(value, TABLE_MAX_ROWS);
    setRowCount(rows);
    setPreviewRows(Math.min(rows, GRID_PREVIEW_ROWS));
    setError(null);
  };

  const handleColInput = (value: number) => {
    const cols = clampDimension(value, TABLE_MAX_COLS);
    setColCount(cols);
    setPreviewCols(Math.min(cols, GRID_PREVIEW_COLS));
    setError(null);
  };

  return (
    <div
      className="cp-modal-backdrop cp-evaluation-plan-sheet-table-create-backdrop"
      style={backdropZIndex != null ? { zIndex: backdropZIndex } : undefined}
      role="presentation"
      onClick={onClose}
    >
      <div
        className="cp-modal cp-evaluation-plan-sheet-table-create-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-evaluation-plan-sheet-table-create-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="cp-evaluation-plan-sheet-table-create-title">표 만들기</h3>
        <div className="cp-modal-body">
          <div
            ref={gridRef}
            className="cp-evaluation-plan-sheet-table-create-grid"
            role="grid"
            aria-label="표 크기 선택"
            onPointerDown={(event) => {
              event.preventDefault();
              const cell = resolveGridCellFromPointer(event.clientX, event.clientY);
              if (!cell) return;
              setIsGridDragging(true);
              applyGridSelection(cell.row, cell.col);
            }}
          >
            {Array.from({ length: GRID_PREVIEW_ROWS }, (_, row) =>
              Array.from({ length: GRID_PREVIEW_COLS }, (_, col) => {
                const selected = row < previewRows && col < previewCols;
                return (
                  <div
                    key={`${row}:${col}`}
                    className={
                      'cp-evaluation-plan-sheet-table-create-grid-cell' + (selected ? ' is-selected' : '')
                    }
                    role="gridcell"
                    aria-selected={selected}
                    onPointerEnter={() => {
                      if (!isGridDragging) return;
                      applyGridSelection(row, col);
                    }}
                  />
                );
              })
            )}
          </div>
          <p className="cp-evaluation-plan-sheet-table-create-grid-size" aria-live="polite">
            {rowCount} x {colCount}
          </p>
          <div className="cp-evaluation-plan-sheet-table-create-dims-row">
            <label className="cp-evaluation-plan-sheet-table-create-dim-field" htmlFor="cp-sheet-table-rows">
              <span className="cp-evaluation-plan-sheet-table-create-label">행 수</span>
              <input
                id="cp-sheet-table-rows"
                type="number"
                min={1}
                max={TABLE_MAX_ROWS}
                className="cp-evaluation-plan-sheet-table-create-input"
                value={rowCount}
                onChange={(e) => handleRowInput(Number(e.target.value))}
              />
            </label>
            <label className="cp-evaluation-plan-sheet-table-create-dim-field" htmlFor="cp-sheet-table-cols">
              <span className="cp-evaluation-plan-sheet-table-create-label">열 수</span>
              <input
                id="cp-sheet-table-cols"
                type="number"
                min={1}
                max={TABLE_MAX_COLS}
                className="cp-evaluation-plan-sheet-table-create-input"
                value={colCount}
                onChange={(e) => handleColInput(Number(e.target.value))}
              />
            </label>
          </div>
          {error ? <p className="cp-evaluation-plan-sheet-table-create-error">{error}</p> : null}
        </div>
        <div className="cp-modal-footer">
          <button type="button" className="cp-btn-secondary" onClick={onClose}>
            취소
          </button>
          <button type="button" className="cp-btn-primary" onClick={handleSubmit}>
            만들기
          </button>
        </div>
      </div>
    </div>
  );
};

export default EvaluationPlanSheetTableCreateModal;
